import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { map, catchError, of } from 'rxjs';
import { environment } from '../../../../../../../environments/environment';
import { SelectPesquisavelComponent } from '../../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { EmptyStateComponent } from '../../../../../../shared/components/empty-state/empty-state.component';
import { SelectPesquisavelService } from '../../../../../../shared/components/select-pesquisavel/select-pesquisavel.service';
import {
    PesquisaCodigoTributacaoModalComponent,
    PesquisaCodigoTributacaoModalData,
    PesquisaCodigoTributacaoModalResult,
} from '../../../../../../shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { MaskService } from '../../../../../../shared/services/mask.service';
import { LookupItemStruct } from '../../../../../../shared/services/structs/pessoa/lookup-item.struct';
import {
    formatNcm as formatNcmUtil,
    badgeLabel as badgeLabelUtil,
    ncmMaisGenericoQueCobre,
    ncmsQueSeTornamRedundantes,
} from '../../regra-tributaria-escopo.util';

import { EscopoProdutoItem } from './escopo-produto-item.struct';

export { EscopoProdutoItem } from './escopo-produto-item.struct';

@Component({
    selector: 'app-regra-tributaria-escopo-step',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        EmptyStateComponent,
        SelectPesquisavelComponent,
        NgxMaskDirective,
    ],
    providers: [provideNgxMask()],
    templateUrl: './regra-tributaria-escopo-step.component.html',
    styleUrl: './regra-tributaria-escopo-step.component.css',
})
export class RegraTributariaEscopoStepComponent {
    @Input({ required: true }) form!: FormGroup;
    @Input() listaRegimeTributario: LookupItemStruct[] = [];
    @Input() listaTipoTributario: LookupItemStruct[] = [];

    public readonly masks = MaskService.getMasks();
    public readonly ncmLookupUrl = environment.urlApiMotorFiscal + 'Lookup/ncm';
    public readonly produtoLookupUrl = environment.urlApiProduto + 'Lookup/listProdutoSelect';
    public readonly produtoControl = new FormControl<number | null>(null);
    // Ids como texto "true"/"false": é assim que a Pessoa (contribuinte de IBS/CBS, booleano) e o
    // Motor Fiscal (comparação de escopo) representam esse valor.
    public readonly contribIbsCbsOptions = [
        { id: 'true', descricao: 'Contribuinte' },
        { id: 'false', descricao: 'Não contribuinte' },
    ];

    public ncmValor = '';
    public produtoSelecionado: any = null;

    private readonly ncmComprimentosValidos = [2, 4, 5, 6, 7, 8];

    constructor(
        private matDialog: MatDialog,
        private selectPesquisavelService: SelectPesquisavelService,
        private alertService: AlertService,
    ) {}

    public get escopoForm(): FormGroup {
        return this.form.get('escopo') as FormGroup;
    }

    public get ncms(): string[] {
        return this.escopoForm.get('ncms')?.value ?? [];
    }

    public get produtos(): EscopoProdutoItem[] {
        return this.escopoForm.get('produtos')?.value ?? [];
    }

    public onNcmKeydown(event: KeyboardEvent): void {
        const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        const isShortcut = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
        if (allowed.includes(event.key) || isShortcut) return;
        if (!/^\d$/.test(event.key)) event.preventDefault();
    }

    public adicionarNcmManual(): void {
        const codigo = (this.ncmValor ?? '').replace(/\D/g, '');
        if (!this.ncmComprimentosValidos.includes(codigo.length)) {
            this.alertService.show('Atenção', 'O NCM deve ter 2, 4, 5, 6, 7 ou 8 dígitos.', AlertType.warning);
            return;
        }
        this.adicionarNcm(codigo);
        this.ncmValor = '';
    }

    /** Adiciona o NCM quando ele não é redundante com outro código já presente na regra. */
    private adicionarNcm(codigo: string): void {
        if (this.ncms.includes(codigo)) return;

        const cobridor = ncmMaisGenericoQueCobre(codigo, this.ncms);
        if (cobridor) {
            this.alertService.show(
                'NCM redundante',
                `O NCM ${this.formatNcm(codigo)} já está coberto pelo NCM ${this.formatNcm(cobridor)}, presente nesta regra. Mantenha apenas o NCM mais genérico.`,
                AlertType.warning,
            );
            return;
        } else {
            const cobertos = ncmsQueSeTornamRedundantes(codigo, this.ncms);
            if (cobertos.length > 0) {
                const lista = cobertos.map(c => this.formatNcm(c)).join(', ');
                this.alertService.show(
                    'NCM redundante',
                    `O NCM ${this.formatNcm(codigo)} já cobre o(s) NCM(s) ${lista}, presente(s) nesta regra. Remova os NCMs específicos antes de adicionar o mais genérico.`,
                    AlertType.warning,
                );
                return;
            }
        }

        this.escopoForm.get('ncms')?.setValue([...this.ncms, codigo]);
    }

    public removerNcm(codigo: string): void {
        this.escopoForm.get('ncms')?.setValue(this.ncms.filter(x => x !== codigo));
    }

    public pesquisarNcm(): void {
        const dialogRef = this.matDialog.open(PesquisaCodigoTributacaoModalComponent, {
            width: '700px',
            maxHeight: '85vh',
            panelClass: 'pesquisa-codigo-tributacao-dialog',
            data: {
                titulo: 'Pesquisar NCM',
                buscarFn: (pesquisa: string) =>
                    this.selectPesquisavelService.getSelectValues<any>(this.ncmLookupUrl, pesquisa).pipe(
                        map(list => ({
                            isError: false,
                            itens: list.map((i: any) => ({ codigo: this.formatNcm(String(i.codigo ?? '')), descricao: i.descricao })),
                        })),
                        catchError(() => of({ isError: true, itens: [] })),
                    ),
                permitirManual: false,
                registrosIniciais: 20,
            } as PesquisaCodigoTributacaoModalData,
        });

        dialogRef.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
            if (!result?.selecionado) return;
            const codigo = result.selecionado.codigo.replace(/\D/g, '');
            this.adicionarNcm(codigo);
        });
    }

    public formatNcm(codigo: string): string {
        return formatNcmUtil(codigo);
    }

    public onProdutoSelecionado(item: any): void {
        this.produtoSelecionado = item;
    }

    public adicionarProduto(): void {
        const item = this.produtoSelecionado;
        if (!item) return;

        const codigo = String(item.idProduto ?? item.id);
        const descricao = item.descricao ?? item.nome ?? codigo;
        const sku = item.codigoSku ?? '';
        if (!this.produtos.some(p => p.codigo === codigo)) {
            this.escopoForm.get('produtos')?.setValue([...this.produtos, { codigo, descricao, sku }]);
        }
        this.produtoSelecionado = null;
        this.produtoControl.setValue(null, { emitEvent: false });
    }

    public removerProduto(item: EscopoProdutoItem): void {
        this.escopoForm.get('produtos')?.setValue(this.produtos.filter(p => p.codigo !== item.codigo));
    }

    public badgeLabel(item: EscopoProdutoItem): string {
        return badgeLabelUtil(item);
    }

    /**
     * Itens selecionados de um escopo múltiplo (contribuinte/regime/tipo) com o rótulo do lookup,
     * para os badges. O id é number nos lookups de regime/tipo tributário e string ("true"/"false")
     * no contribuinte de IBS/CBS.
     */
    public escopoSelecionados(controlName: string, lista: { id: number | string; descricao: string }[]): { id: number | string; label: string }[] {
        const selecionados: (number | string)[] = this.escopoForm.get(controlName)?.value ?? [];
        return selecionados.map(id => ({ id, label: lista.find(o => o.id === id)?.descricao ?? String(id) }));
    }

    public removerEscopoSelecionado(controlName: string, id: number | string): void {
        const selecionados: (number | string)[] = this.escopoForm.get(controlName)?.value ?? [];
        this.escopoForm.get(controlName)?.setValue(selecionados.filter(x => x !== id));
    }
}
